import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AlarmProfile } from "@src/cloud-connect/alarms/entities/alarm-profile";
import store, { RootState } from "@src/reducers/store";
import { AlarmList as AlarmListInstance } from "@src/cloud-connect/alarms/entities/alarm-list";
import {
  getAlarmProfiles,
  getAlarmProfilesReferenceCopy,
} from "@src/cloud-connect/dependency-injection-container/get-alarm-profiles";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { parseAlarmProfiles } from "@src/cloud-connect/alarms/entities/alarm-profiles-parser";
import { AlarmRow } from "@src/cloud-connect/alarms/entities/alarm-row";
import { transferAlarmProfilesToClientUseCase } from "@src/cloud-connect/alarms/use-cases/transfer-alarm-profiles-to-client";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { fetchActiveTimeseriesForAlarmList } from "@src/cloud-connect/api/timeseries/fetch-active-timeseries-for-alarm-list";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { i18n } from "@src/i18n/i18n";
import { getAlarmProfilesUseCase } from "@src/cloud-connect/alarms/use-cases/get-alarm-profile-use-case";
import { findSearchedAlarmRows } from "./alarm-list-search";

export interface ActiveTimeseriesResponseTypes {
  data_id: number;
  tsid: string;
  namespace: string;
  group: string;
  source: string;
  data_type: string;
  mime_type: string | null;
  unit: number | null;
  name: string | null;
}

export type ActiveTimeseriesResponseMap = Map<
  string,
  ActiveTimeseriesResponseTypes
>;

const EMPTY_ALARM_ROWS: AlarmRow[] = [];

type SearchMeta = {
  isActive: boolean;
  searchTerms: string[];
};

export const useAlarmListNewHook = () => {
  const [arrayOfAlarmRows, setArrayOfAlarmRows] = useState<AlarmRow[]>();
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [activeTimeseriesResponse, setActiveTimeseriesResponse] =
    useState<ActiveTimeseriesResponseMap>(new Map());
  const [alarmListInstance, setAlarmListInstance] =
    useState<AlarmListInstance>();
  const [transferAlarmLoading, setTransferAlarmLoading] = useState(false);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const alarmNodeSelectionState = useSelector(
    (state: RootState) => state.yourSlice.alarmNodeSelection.state,
  );
  const sourceId = useSelector(
    (state: RootState) => state.yourSlice.connectionView.source.source_id,
  );
  const clientId = useSelector(
    (state: RootState) =>
      state.yourSlice.connectionView.source.connected_via_client,
  );

  const alarmProfileInstance = getAlarmProfiles();

  const onActiveProfileChange = useCallback(() => {
    if (activeTimeseriesResponse && activeTimeseriesResponse.size > 0) {
      createAndSetAlarmList(
        alarmProfileInstance.getActiveAlarmProfile(),
        Array.from(activeTimeseriesResponse.values()),
        setAlarmListInstance,
        setArrayOfAlarmRows,
      );
    }
  }, [activeTimeseriesResponse, alarmProfileInstance]);

  useAlarmProfileObserver(alarmProfileInstance, onActiveProfileChange);

  useEffect(() => {
    if (!arrayOfAlarmRows || arrayOfAlarmRows.length === 0) {
      setSearchTerms([]);
    }
  }, [arrayOfAlarmRows]);

  const handleSearchResults = useCallback((meta: SearchMeta) => {
    setIsSearchLoading(true);
    setSearchTerms(meta.searchTerms);
  }, []);

  const cloneAlarmProfilesToCreateReferenceCopy = () => {
    const alarmProfilesInstanceAfterRequest = getAlarmProfiles();
    const clonedAlarmProfiles = alarmProfilesInstanceAfterRequest.clone();
    getAlarmProfilesReferenceCopy().setAlarmProfiles(
      clonedAlarmProfiles.getAlarmProfiles(),
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const updateAlarmData = async (signal?: AbortSignal) => {
      const { parsedAlarmProfiles, channelsMap } = await fetchAndParseAlarmData(
        clientId,
        sourceId,
        signal,
      );

      setActiveTimeseriesResponse(channelsMap);

      alarmProfileInstance.setAlarmProfiles(
        parsedAlarmProfiles.getAlarmProfiles(),
      );

      cloneAlarmProfilesToCreateReferenceCopy();

      createAndSetAlarmList(
        alarmProfileInstance.getActiveAlarmProfile(),
        Array.from(channelsMap.values()),
        setAlarmListInstance,
        setArrayOfAlarmRows,
      );
    };

    updateAlarmData(signal);

    return () => {
      controller.abort();
    };
  }, [sourceId]);

  const transferAlarmProfilesToClient = async () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    await transferAlarmProfilesToClientUseCase(setTransferAlarmLoading);
  };

  const isTransferAlarmProfilesToClientActive = () => {
    return (
      typeof arrayOfAlarmRows !== "undefined" &&
      authContext.permissions.includes("manage-condition-monitoring")
    );
  };

  const hasActiveSearch = searchTerms.length > 0;

  const showAlarmListRow = useMemo(() => {
    if (!hasActiveSearch) return arrayOfAlarmRows;
    return findSearchedAlarmRows(
      arrayOfAlarmRows ?? EMPTY_ALARM_ROWS,
      searchTerms,
    );
  }, [arrayOfAlarmRows, hasActiveSearch, searchTerms]);

  useEffect(() => {
    if (!isSearchLoading) return;
    setIsSearchLoading(false);
  }, [isSearchLoading, showAlarmListRow]);

  const isEmptyAlarmsData = !showAlarmListRow?.length;
  const showNoSearchResults = hasActiveSearch && isEmptyAlarmsData;
  // Keep the search bar based on source data so it stays visible when a search returns no results, letting the user clear or edit the query.
  const showSearchBar = !!arrayOfAlarmRows?.length;

  return {
    alarmNodeSelectionState,
    arrayOfAlarmRows,
    handleSearchResults,
    showAlarmListRow,
    isSearchLoading,
    isEmptyAlarmsData,
    showNoSearchResults,
    showSearchBar,
    alarmListInstance,
    transferAlarmProfilesToClient,
    isTransferAlarmProfilesToClientActive,
    transferAlarmLoading,
  };
};

// Helper Functions

const createAndSetAlarmList = (
  alarmProfile: AlarmProfile,
  channelsResponse: ActiveTimeseriesResponseTypes[],
  setAlarmListInstance: React.Dispatch<React.SetStateAction<AlarmListInstance>>,
  setArrayOfAlarmRows: React.Dispatch<React.SetStateAction<AlarmRow[]>>,
) => {
  const alarmListInstance = new AlarmListInstance(
    alarmProfile,
    getMachineStructureNodeset().getFlatMap(),
    channelsResponse,
  );

  setAlarmListInstance(alarmListInstance);
  setArrayOfAlarmRows(alarmListInstance.getAlarmRows() as AlarmRow[]);
};

const fetchAndParseAlarmData = async (
  clientId: string,
  sourceId: string,
  signal?: AbortSignal,
) => {
  const language = store.getState().yourSlice.language;
  const texts = {
    undefinedChannelsObjectError: i18n(
      "modules.alarmList.undefinedChannelsObjectError",
      language,
    ),
  };

  const alarmProfilesResponse = await getAlarmProfilesUseCase(signal);
  if (signal?.aborted) return;

  const parsedAlarmProfiles = await parseAlarmProfiles({
    response: alarmProfilesResponse.body,
    getNodeById: getMachineStructureNodeset().getNodeById.bind(
      getMachineStructureNodeset(),
    ),
    OurNodeClass: OurNode,
    source_id: sourceId,
    client_id: clientId,
  });

  const channelsObject = await fetchActiveTimeseriesForAlarmList(
    sourceId,
    signal,
  );
  if (signal?.aborted) return;

  if (!channelsObject) {
    errorSnackbar(texts.undefinedChannelsObjectError);
    return { parsedAlarmProfiles, channelsMap: new Map() };
  }

  const channelsMap = new Map<string, ActiveTimeseriesResponseTypes>(
    Object.entries(channelsObject),
  );

  return { parsedAlarmProfiles, channelsMap };
};

const useAlarmProfileObserver = (
  alarmProfileInstance: ReturnType<typeof getAlarmProfiles>,
  onActiveProfileChange: () => void,
) => {
  useEffect(() => {
    alarmProfileInstance.addObserver(onActiveProfileChange);
    return () => alarmProfileInstance.removeObserver(onActiveProfileChange);
  }, [alarmProfileInstance, onActiveProfileChange]);
};
